import type { QueryClient } from "@tanstack/react-query";
import { CLOUD_VENDOR, DESTINATION_TYPE } from "@/server/types/export";
import type { CloudVendor, DestinationType, FhirAuthType } from "@/server/types/export";
import type {
  DestinationDialogType,
  ObjectStorageFormState,
  FormDrafts,
  VendorUploadConfig,
} from "@/frontend/types/dashboard/settings/export";
import type { orpc } from "@/frontend/orpc-client";

export type VendorFieldConfig = {
  endpointPlaceholder: string;
  regionLabel: string;
  regionPlaceholder: string;
  bucketLabel: string;
  bucketPlaceholder: string;
  accessKeyLabel: string;
  accessKeyPlaceholder: string;
  secretKeyLabel: string;
  secretKeyPlaceholder: string;
};

export const vendorFieldConfig: Record<CloudVendor, VendorFieldConfig> = {
  aws: {
    endpointPlaceholder: "https://s3.us-east-1.amazonaws.com",
    regionLabel: "Region",
    regionPlaceholder: "e.g. us-east-1",
    bucketLabel: "Bucket",
    bucketPlaceholder: "bucket-name",
    accessKeyLabel: "Access Key ID",
    accessKeyPlaceholder: "AWS access key id",
    secretKeyLabel: "Secret Access Key",
    secretKeyPlaceholder: "AWS secret access key",
  },
  gcs: {
    endpointPlaceholder: "https://storage.googleapis.com",
    regionLabel: "Project ID",
    regionPlaceholder: "your-gcp-project-id",
    bucketLabel: "Bucket",
    bucketPlaceholder: "gcs-bucket-name",
    accessKeyLabel: "Service Account Email",
    accessKeyPlaceholder: "service-account@project.iam.gserviceaccount.com",
    secretKeyLabel: "Private Key",
    secretKeyPlaceholder: "-----BEGIN PRIVATE KEY-----",
  },
  azure: {
    endpointPlaceholder: "https://account.blob.core.windows.net",
    regionLabel: "Region",
    regionPlaceholder: "optional-region-or-default",
    bucketLabel: "Container",
    bucketPlaceholder: "container-name",
    accessKeyLabel: "Account Name",
    accessKeyPlaceholder: "storage-account-name",
    secretKeyLabel: "Account Key",
    secretKeyPlaceholder: "Azure storage account key",
  },
};

/**
 * Upload configuration for vendors that support credential file uploads.
 * Only GCS and AWS are supported — Azure has no standard credential file format.
 */
export const vendorUploadConfig: Partial<Record<CloudVendor, VendorUploadConfig>> = {
  gcs: {
    accept: ".json",
    buttonLabel: "Upload .json",
    description: "Upload your GCS service account key",
    parse: parseGcsCredentials,
  },
  aws: {
    accept: ".csv",
    buttonLabel: "Upload .csv",
    description: "Upload your AWS credentials",
    parse: parseAwsCredentials,
  },
};

/**
 * Computes the progress fill for an export run or destination card. FHIR-server
 * destinations use the deduped org-wide resource counter (`resources_posted /
 * resources_total`); everything else falls back to the per-account counter
 * (`accounts_processed / accounts_total`). Postgres count aggregates arrive as
 * `string` via bigint coercion, so everything is cast through `Number()`.
 *
 * @param input - Either an active-run field bag (destination list row) or a
 *   full export_run row; both shapes are accepted
 * @returns total, done, and the rounded percent 0–100
 */
export function computeExportProgress(input: {
  destination_type: DestinationType | string;
  resources_total?: number | string | null;
  resources_posted?: number | string | null;
  activeRunResourcesTotal?: number | string | null;
  activeRunResourcesPosted?: number | string | null;
  accounts_total?: number | string | null;
  accounts_processed?: number | string | null;
  activeRunAccountsTotal?: number | string | null;
  activeRunAccountsProcessed?: number | string | null;
}): { total: number; done: number; pct: number } {
  const isFhir = input.destination_type === DESTINATION_TYPE.FHIR_SERVER;

  const resourcesTotal = Number(
    input.resources_total ?? input.activeRunResourcesTotal ?? 0
  );
  const resourcesPosted = Number(
    input.resources_posted ?? input.activeRunResourcesPosted ?? 0
  );
  const accountsTotal = Number(
    input.accounts_total ?? input.activeRunAccountsTotal ?? 0
  );
  const accountsProcessed = Number(
    input.accounts_processed ?? input.activeRunAccountsProcessed ?? 0
  );

  const total = isFhir ? resourcesTotal : accountsTotal;
  const done = isFhir ? resourcesPosted : accountsProcessed;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  return { total, done, pct };
}

export function humanizeExportError(
  message: string | null | undefined
): string {
  if (!message) return "Export could not be completed.";

  if (message.includes("Access Denied") || message.includes("AccessDenied")) {
    return "Access Denied — check that the credentials have the required permissions.";
  }
  if (message.includes("NoSuchBucket") || message.includes("bucket")) {
    return "The specified bucket/container was not found. Check the name and region.";
  }
  if (message.includes("401") || message.includes("Unauthorized")) {
    return "Authentication failed — verify your credentials.";
  }
  if (message.includes("ECONNREFUSED") || message.includes("timeout")) {
    return "Connection failed — the server is unreachable. Check the endpoint URL.";
  }

  return message;
}

export function formatTimeAgo(date: string | Date | null): string {
  if (!date) return "Never";
  const ms = Date.now() - new Date(date).getTime();
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return "Just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function formatDuration(
  startedAt: string | Date | null,
  completedAt: string | Date | null
): string {
  if (!startedAt) return "—";
  const start = new Date(startedAt).getTime();
  const end = completedAt ? new Date(completedAt).getTime() : Date.now();
  const seconds = Math.floor((end - start) / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes < 60) {
    return remainingSeconds > 0
      ? `${minutes}m ${remainingSeconds}s`
      : `${minutes}m`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
}

export type DestinationTypeOption = {
  type: "aws" | "gcs" | "azure" | "fhir_server";
  destinationType: "object_storage" | "fhir_server";
  label: string;
  description: string;
};

export function initialFormDrafts(): FormDrafts {
  const storageDefaults: ObjectStorageFormState = {
    name: "", endpoint: "", region: "", bucket: "",
    accessKeyId: "", secretAccessKey: "", fileType: "ndjson",
  };
  return {
    aws: { ...storageDefaults },
    gcs: { ...storageDefaults },
    azure: { ...storageDefaults },
    fhir_server: {
      name: "", fhirServerUrl: "", authType: "bearer_token",
      tokenEndpoint: "", clientId: "", clientSecret: "",
      scope: "", bearerToken: "", isInternal: false,
    },
  };
}

export function getDialogType(dest: any): DestinationDialogType {
  if (dest.destination_type === DESTINATION_TYPE.FHIR_SERVER) return DESTINATION_TYPE.FHIR_SERVER;
  return (dest.cloud_vendor ?? CLOUD_VENDOR.AWS) as DestinationDialogType;
}

/**
 * Fetches a destination by id (including secrets via `getExportDestination`)
 * and returns the dialog type + the single form-draft slice to merge into
 * `formDrafts`. Centralizes the "prepare edit dialog" logic so both the
 * destinations-list and the destination-detail view stay in sync.
 *
 * @param queryClient - React Query client for the imperative fetch
 * @param orpcProxy - The oRPC TanStack utils, typically from `@/frontend/orpc-client`
 * @param destinationId - The export destination to load
 * @returns The dialog type and a partial FormDrafts with only the one matching key set
 */
export async function loadEditDestinationDraft(
  queryClient: QueryClient,
  orpcProxy: typeof orpc,
  destinationId: string,
): Promise<{ type: DestinationDialogType; drafts: Partial<FormDrafts> }> {
  const full = await queryClient.fetchQuery(
    orpcProxy.admin.getExportDestination.queryOptions({ input: { destinationId } }),
  );
  const type = getDialogType(full);

  if (full.destination_type === DESTINATION_TYPE.FHIR_SERVER) {
    return {
      type,
      drafts: {
        fhir_server: {
          name: full.name ?? "",
          fhirServerUrl: full.fhir_server_url ?? "",
          authType: (full.auth_type ?? "none") as FhirAuthType,
          tokenEndpoint: full.token_endpoint ?? "",
          clientId: full.client_id ?? "",
          clientSecret: full.client_secret ?? "",
          scope: full.scope ?? "",
          bearerToken: full.bearer_token ?? "",
          isInternal: full.is_internal ?? false,
        },
      },
    };
  }

  const vendorKey = (full.cloud_vendor ?? CLOUD_VENDOR.AWS) as "aws" | "gcs" | "azure";
  return {
    type,
    drafts: {
      [vendorKey]: {
        name: full.name ?? "",
        endpoint: full.endpoint ?? "",
        region: full.region ?? "",
        bucket: full.bucket ?? "",
        accessKeyId: full.access_key_id ?? "",
        secretAccessKey: full.secret_access_key ?? "",
        fileType: (full.file_type ?? "ndjson") as "ndjson" | "zipped-ndjson",
      },
    },
  };
}

export const destinationTypes: DestinationTypeOption[] = [
  {
    type: CLOUD_VENDOR.AWS,
    destinationType: DESTINATION_TYPE.OBJECT_STORAGE,
    label: "AWS S3",
    description: "Export NDJSON to S3",
  },
  {
    type: CLOUD_VENDOR.GCS,
    destinationType: DESTINATION_TYPE.OBJECT_STORAGE,
    label: "Google Cloud Storage",
    description: "Export NDJSON to GCS",
  },
  {
    type: CLOUD_VENDOR.AZURE,
    destinationType: DESTINATION_TYPE.OBJECT_STORAGE,
    label: "Azure Blob",
    description: "Export NDJSON to Azure",
  },
  {
    type: DESTINATION_TYPE.FHIR_SERVER,
    destinationType: DESTINATION_TYPE.FHIR_SERVER,
    label: "FHIR Server",
    description: "Push bundles to a FHIR endpoint",
  },
];

/**
 * Parses a GCS service account credentials.json file.
 * Extracts client_email, private_key, and optionally project_id.
 * Throws a user-facing error message if the file is invalid.
 *
 * Steps:
 * 1. Attempt JSON.parse; throw on invalid JSON.
 * 2. Validate required fields client_email and private_key are non-empty strings.
 * 3. Build masked displayFields (project_id row included only when present).
 * 4. Return structured result with GCS endpoint hardcoded.
 *
 * @param text - Raw text content of the uploaded JSON file
 * @returns Parsed form field values and masked display fields for the summary card
 */
export function parseGcsCredentials(text: string): {
  accessKeyId: string;
  secretAccessKey: string;
  region?: string;
  endpoint: string;
  displayFields: { label: string; value: string }[];
} {
  let parsed: Record<string, unknown>;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error(
      "Invalid file — expected a GCS service account JSON with client_email and private_key fields"
    );
  }

  const clientEmail = parsed.client_email;
  const privateKey = parsed.private_key;
  const projectId = parsed.project_id;

  if (
    typeof clientEmail !== "string" || !clientEmail ||
    typeof privateKey !== "string" || !privateKey
  ) {
    throw new Error(
      "Invalid file — expected a GCS service account JSON with client_email and private_key fields"
    );
  }

  const displayFields: { label: string; value: string }[] = [
    { label: "Service Account", value: clientEmail },
  ];
  if (typeof projectId === "string" && projectId) {
    displayFields.push({ label: "Project ID", value: projectId });
  }
  displayFields.push({ label: "Private Key", value: "••••••••••••" });

  return {
    accessKeyId: clientEmail,
    secretAccessKey: privateKey,
    region: typeof projectId === "string" && projectId ? projectId : undefined,
    endpoint: "https://storage.googleapis.com",
    displayFields,
  };
}

/**
 * Parses an AWS credentials.csv file (downloaded from IAM console).
 * Extracts Access Key ID and Secret Access Key from the CSV data row.
 * Throws a user-facing error message if the file is invalid.
 *
 * Steps:
 * 1. Split text on line boundaries, filtering empty lines.
 * 2. Require at least two lines (header + data row); throw otherwise.
 * 3. Split data row on comma; require two non-empty columns.
 * 4. Mask the Access Key ID (first 4 + 8 dots + last 4 chars) for display.
 * 5. Return structured result with masked displayFields.
 *
 * @param text - Raw text content of the uploaded CSV file
 * @returns Parsed form field values and masked display fields for the summary card
 */
export function parseAwsCredentials(text: string): {
  accessKeyId: string;
  secretAccessKey: string;
  displayFields: { label: string; value: string }[];
} {
  const lines = text.trim().split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) {
    throw new Error(
      "Invalid file — expected an AWS credentials CSV with Access key ID and Secret access key columns"
    );
  }

  const dataLine = lines[1];
  if (!dataLine) {
    throw new Error(
      "Invalid file — expected an AWS credentials CSV with Access key ID and Secret access key columns"
    );
  }
  const dataRow = dataLine.split(",");
  const rawKeyId = dataRow[0];
  const rawSecret = dataRow[1];
  if (!rawKeyId || !rawSecret || !rawKeyId.trim() || !rawSecret.trim()) {
    throw new Error(
      "Invalid file — expected an AWS credentials CSV with Access key ID and Secret access key columns"
    );
  }

  const accessKeyId = rawKeyId.trim();
  const secretAccessKey = rawSecret.trim();

  const maskedKeyId =
    accessKeyId.length > 8
      ? accessKeyId.slice(0, 4) + "••••••••" + accessKeyId.slice(-4)
      : "••••••••••••";

  return {
    accessKeyId,
    secretAccessKey,
    displayFields: [
      { label: "Access Key ID", value: maskedKeyId },
      { label: "Secret Access Key", value: "••••••••••••" },
    ],
  };
}
